import React from "react";
import { AlertCircle, RefreshCw } from "lucide-react";

function isHtmlBlob(s) {
  if (typeof s !== "string") return false;
  if (s.length > 1500) return true;
  return /<\/?(html|body|head|script|style|h1|pre|center)\b/i.test(s);
}

/**
 * Boundary scoped to the portal — never dumps raw HTML on screen.
 * Catches both component-tree errors AND unhandled promise rejections that
 * leak from useEffect/setTimeout callbacks.
 */
export default class PortalErrorBoundary extends React.Component {
  state = { error: null, transient: null };

  componentDidMount() {
    this._onReject = (e) => {
      const r = e.reason;
      const msg = r?.message || (typeof r === "string" ? r : "Something went wrong. Please refresh and try again.");
      const clean = isHtmlBlob(msg) ? "The server is temporarily unavailable. Please refresh or try again." : msg;
      this.setState({ transient: clean });
      // Auto-dismiss after 6s so it doesn't linger.
      clearTimeout(this._dismissTimer);
      this._dismissTimer = setTimeout(() => this.setState({ transient: null }), 6000);
      // Prevent React DevTools dev overlay from displaying the HTML.
      e.preventDefault?.();
    };
    window.addEventListener("unhandledrejection", this._onReject);
  }

  componentWillUnmount() {
    window.removeEventListener("unhandledrejection", this._onReject);
    clearTimeout(this._dismissTimer);
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("Portal boundary caught:", error, info);
  }

  render() {
    const { error, transient } = this.state;
    if (error) {
      const raw = error?.message || "Something went wrong";
      const msg = isHtmlBlob(raw) ? "Something went wrong. Please refresh the page." : raw;
      return (
        <div className="min-h-screen bg-[#FAF5EC] flex items-center justify-center p-6">
          <div className="bg-white border border-[#6E1F2C]/20 max-w-[480px] w-full p-8 text-center">
            <AlertCircle className="mx-auto text-[#A3141A] mb-3" size={32} />
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#A3141A] mb-2">Something went wrong</p>
            <h2 className="font-serif-display text-xl text-[#1C110C] mb-3">The portal hit an unexpected error</h2>
            <p className="text-sm text-[#3E2A22] mb-5">{msg}</p>
            <button
              onClick={() => { this.setState({ error: null }); window.location.reload(); }}
              data-testid="portal-error-reload"
              className="inline-flex items-center gap-2 bg-[#6E1F2C] text-[#FAF5EC] px-5 py-2.5 text-[11px] tracking-[0.28em] uppercase hover:bg-[#56151F]"
            >
              <RefreshCw size={13} /> Reload portal
            </button>
          </div>
        </div>
      );
    }
    return (
      <>
        {this.props.children}
        {transient && (
          <div
            data-testid="portal-toast-error"
            role="alert"
            className="fixed bottom-6 right-6 z-[200] max-w-[380px] bg-white border border-[#A3141A]/30 shadow-lg p-4 flex items-start gap-3"
          >
            <AlertCircle className="text-[#A3141A] mt-0.5 shrink-0" size={18} />
            <div className="flex-1 min-w-0">
              <p className="text-[10px] tracking-[0.28em] uppercase text-[#A3141A] mb-1">Error</p>
              <p className="text-sm text-[#1C110C] break-words">{transient}</p>
            </div>
            <button
              onClick={() => this.setState({ transient: null })}
              className="text-[#7A6B5F] hover:text-[#1C110C] text-xs"
              aria-label="Dismiss"
            >✕</button>
          </div>
        )}
      </>
    );
  }
}
